import React, { useState } from "react";
import { Box, Tooltip, makeStyles } from "@material-ui/core";
import { resolveItem } from "./reviewApi";
import TemplatePreview from "./TemplatePreview";
import { heldOutcome, isHeld, isOpenItem, itemFlagReason, itemStatus } from "./caseText";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import { BusinessContextSection, CategoryField, DialogSection, Field, ImpactBox, LEGIT_TOGGLE, ReviewDialog, SegmentedToggle } from "./ReviewDialog";
import {
  CONSEQUENCE,
  SUBJECTS,
  Section,
  StatusPill,
  TextAction,
  cap,
  contextBody,
  contentVerdict,
  contextFrom,
  contextReady,
  errMsg,
  fmtDate,
  fromNow,
  harmDefault,
  humanizePolicy,
  humanizeSignal,
  isMachineText,
  labelWord,
  legitFor,
  pal,
  readableExplanation,
  sentenceCase,
  shortAgo,
  shortId,
  subjectOf,
} from "./reviewUi";
import { useSnackbarContext } from "../../commons/Snackbar/Snackbar";

// EVIDENCE: every flagged item, per subject type. Open items first; reviewed
// ones fold under "Reviewed (n)".

const useStyles = makeStyles((theme) => {
  const c = pal(theme);
  return {
    switch: { display: "flex", gap: 18, fontSize: 12.5, marginBottom: 4 },
    switchOpt: { cursor: "pointer", color: c.hint, background: "none", border: 0, padding: 0, font: "inherit", "&:hover": { color: c.text2 } },
    switchOn: { color: c.primary, fontWeight: 600, "&:hover": { color: c.primary } },
    list: { borderTop: `1px solid ${c.line}` },
    item: {
      display: "grid",
      gridTemplateColumns: "280px minmax(0, 1fr)",
      gap: 40,
      padding: "20px 0",
      borderBottom: `1px solid ${c.line}`,
      "@media (max-width: 1240px)": { gridTemplateColumns: "240px minmax(0, 1fr)", gap: 28 },
    },
    hint: { color: c.text2, fontSize: 12.5, marginTop: 6, maxWidth: 520 },
    line: { color: c.text, fontSize: 13, marginTop: 10, maxWidth: 560, lineHeight: 1.5, "& + $line": { marginTop: 4 } },
    k: { color: c.hint, marginRight: 6 },
    expl: { color: c.text2, marginTop: 6, maxWidth: 440 },
    meta: { color: c.hint, fontSize: 12, marginTop: 8, maxWidth: 520 },
    acts: { marginTop: 12, display: "flex", gap: 16 },
    errored: { color: c.error, fontSize: 12, marginTop: 6 },
    reviewed: { color: c.text2, fontSize: 12.5, marginTop: 10, maxWidth: 560 },
    wrong: { color: c.error, opacity: 0.75, whiteSpace: "nowrap" },
    match: { color: c.hint, fontSize: 12, marginTop: 6, maxWidth: 520 },
    toggle: { marginTop: 14 },
    empty: { color: c.hint, fontSize: 13, padding: "4px 0" },
  };
});

// "Reviewed by Priya as transactional · 2h ago · model said phishing · model was wrong"
// The pill above already says what happened (Approved — sent to Meta, …).
function Outcome({ review, held }) {
  const s = useStyles();
  const { label, modelLabel, modelCorrect } = review;
  const outcome = heldOutcome(held);
  const released = ["submitted", "meta_rejected", "template_gone"].includes(outcome);
  const saidOther = modelLabel && modelLabel !== label;
  const metaReason = outcome === "meta_rejected" ? held.metaError || held.reason : null;
  return (
    <div className={s.reviewed}>
      {metaReason ? <div className={s.wrong} style={{ whiteSpace: "normal" }}>Meta's reason: {metaReason}</div> : null}
      Reviewed by {review.by || "—"}
      {label ? ` as ${labelWord(label)}${modelCorrect === true ? " (confirmed)" : ""}` : ""} ·{" "}
      {fromNow((released && held.releasedAt) || review.at)}
      {saidOther ? ` · model said ${labelWord(modelLabel)}` : ""}
      {modelCorrect === false ? (
        <>
          {" · "}
          <span className={s.wrong}>model was wrong</span>
        </>
      ) : null}
      {review.note ? ` · “${review.note}”` : ""}
    </div>
  );
}

function Item({ item, canWrite, canDelete, forced, businessContext, onChanged }) {
  const s = useStyles();
  const { showSnackbar } = useSnackbarContext() || {};
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(""); // "" | "ok" | "delete"
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  // Mark OK / Approve asks for the business context (prefilled from the owner).
  const [ctx, setCtx] = useState(() => contextFrom(businessContext));
  const open = isOpenItem(item);
  // Held: blocked and not at Meta yet; approving submits it, deleting rejects it.
  const held = open && isHeld(item);
  const review = item.review || {};
  const content = item.content || {};
  // What happened to the template, what the checker thinks, and why it's here.
  const status = itemStatus(item);
  const flagReason = itemFlagReason(item, forced);
  const [okLabel, setOkLabel] = useState(() => legitFor(item.template?.category));
  const [delLabel, setDelLabel] = useState(() => harmDefault(content.primaryLabel));

  const act = async (action, note, label) => {
    setBusy(action);
    setError("");
    try {
      const res = await resolveItem(item._id, action, note, label, action === "ok" ? contextBody(ctx) : undefined);
      const r = (res && res.review) || {};
      // Meta can refuse a released template; the item is then rejected.
      // Backend: res.release = { outcome: "submitted" | "meta_rejected" | "template_gone", reason? }
      const release = res && res.release;
      const metaRejected = held && action === "ok" && release && release.outcome === "meta_rejected";
      if (metaRejected) {
        showSnackbar && showSnackbar(release.reason ? `Meta rejected the template: ${release.reason}` : r.note || "Meta rejected the template", "error");
      } else if (held && action === "ok" && release && release.outcome === "template_gone") {
        showSnackbar && showSnackbar("Closed — the customer had already deleted this template", "info");
      } else if (showSnackbar) {
        const done = held ? { ok: "Sent to Meta", delete: "Rejected — never sent" } : { ok: "Marked OK", delete: "Template deleted" };
        showSnackbar(done[action], "success");
      }
      setConfirm("");
      onChanged();
    } catch (e) {
      const status = e?.response?.status;
      const msg = held && action === "ok" && status === 502 ? "Couldn't reach Meta — try again" : errMsg(e, "Couldn't update this item");
      setError(msg);
      if (status === 409 || status === 404) onChanged();
    } finally {
      setBusy("");
    }
  };
  const ask = (key) => {
    setError("");
    setNote("");
    if (key === "ok") setCtx(contextFrom(businessContext));
    setConfirm(key);
  };
  const itemName = item.templateName || item.template?.name || "this item";
  const cancel = () => {
    setConfirm("");
    setError("");
  };

  const explanation = readableExplanation(item);
  const machine = isMachineText(content.explanation);
  // Escalating policies are already in "Flagged because"; tag the rest.
  const words = [
    ...(content.signals || []).map(humanizeSignal),
    ...(item.policy?.categories || [])
      .filter((cat) => cat && cat.tier && !["review", "block"].includes(cat.tier))
      .map((cat) => `${humanizePolicy(cat.id)} policy`),
  ];
  const meta = [
    ...words.map((w, i) => (i === 0 ? cap(w) : w)),
    item.templateName || item.template?.name,
    item.createdAt ? `${shortAgo(item.createdAt)} ago` : null,
  ].filter(Boolean);
  const details = [
    item.channel ? `Channel ${sentenceCase(item.channel)}` : null,
    item.agentId ? `Agent ${shortId(item.agentId)}` : null,
    item.deviceId ? `Device ${item.deviceId}` : null,
    item.metaTemplateId ? `Meta id ${item.metaTemplateId}` : null,
    content.confidence ? `${sentenceCase(content.confidence)} confidence` : null,
    (item.models || []).length ? `Models ${item.models.join(", ")}` : null,
    machine ? `Model text: ${content.explanation}` : null,
    item.createdAt ? fmtDate(item.createdAt) : null,
  ].filter(Boolean);

  return (
    <div className={s.item}>
      <TemplatePreview item={item} />
      <div style={{ minWidth: 0 }}>
        <StatusPill status={status} />
        {status && status.open && status.hint ? <div className={s.hint}>{status.hint}</div> : null}
        {item.status === "error" ? (
          <div className={s.errored}>Screening errored{item.errorReason ? ` (${item.errorReason})` : ""}; the verdict may be incomplete.</div>
        ) : null}
        <div className={s.line}>
          <span className={s.k}>Content:</span>
          {contentVerdict(item)}
        </div>
        {flagReason ? (
          <div className={s.line}>
            <span className={s.k}>Flagged because:</span>
            {flagReason}
          </div>
        ) : null}
        {explanation ? <div className={s.expl}>{explanation}</div> : null}
        {item.humanApprovedMatch ? (
          <div className={s.match}>A reviewer approved this exact template before, so it's only FYI.</div>
        ) : null}
        {meta.length ? (
          <Tooltip title={details.join(" · ")} arrow placement="bottom-start">
            <div className={s.meta}>{meta.join(" · ")}</div>
          </Tooltip>
        ) : null}
        {open ? (
          canWrite ? (
            <div className={s.acts}>
              <TextAction inline tone="primary" disabled={!!busy || !!confirm} onClick={() => ask("ok")}>
                {held ? "Approve & send to Meta" : "Mark OK"}
              </TextAction>
              {canDelete ? (
                <TextAction inline tone="danger" disabled={!!busy || !!confirm} onClick={() => ask("delete")}>
                  {held ? "Reject" : "Delete template"}
                </TextAction>
              ) : null}
            </div>
          ) : null
        ) : (
          <Outcome review={review} held={item.held} />
        )}
        {error && !confirm ? <div className={s.errored}>{error}</div> : null}
        {confirm === "ok" ? (
          <ReviewDialog
            title={held ? "Approve & send to Meta" : "Mark OK"}
            subtitle={
              <>
                <span>{itemName}</span>
                <StatusPill status={status} style={{ fontSize: 11.5 }} />
              </>
            }
            confirmLabel={held ? "Approve & send to Meta" : "Mark OK"}
            busy={busy === "ok"}
            confirmDisabled={!contextReady(ctx, businessContext)}
            error={error}
            onConfirm={() => act("ok", note.trim(), okLabel)}
            onCancel={cancel}
          >
            <DialogSection label="What happens">
              <ImpactBox
                lines={[
                  held ? "This template is sent to Meta exactly as written" : "This item is marked OK",
                  "Your label helps train the checker",
                ]}
              />
            </DialogSection>
            <DialogSection label="Correct label">
              <SegmentedToggle options={LEGIT_TOGGLE} value={okLabel} onChange={setOkLabel} disabled={busy === "ok"} ariaLabel="Correct label" />
            </DialogSection>
            <BusinessContextSection value={ctx} onChange={setCtx} disabled={busy === "ok"} />
            <DialogSection label="Reviewer note">
              <Field label="What did the model get wrong? (optional)" value={note} onChange={setNote} max={500} disabled={busy === "ok"} single />
            </DialogSection>
          </ReviewDialog>
        ) : null}
        {confirm === "delete" ? (
          <ReviewDialog
            size="xs"
            title={held ? "Reject template" : "Delete template"}
            subtitle={itemName}
            danger
            confirmLabel={held ? "Reject" : "Delete template"}
            busy={busy === "delete"}
            error={error}
            onConfirm={() => act("delete", note.trim(), delLabel)}
            onCancel={cancel}
          >
            <ImpactBox tone="danger" icon={DeleteOutline} lines={[held ? CONSEQUENCE.reject : CONSEQUENCE.delete]} />
            <DialogSection>
              <div style={{ marginTop: 16 }}>
                <CategoryField value={delLabel} onChange={setDelLabel} modelLabel={content.primaryLabel} disabled={busy === "delete"} />
                <Field label="Note (optional)" value={note} onChange={setNote} max={500} disabled={busy === "delete"} single />
              </div>
            </DialogSection>
          </ReviewDialog>
        ) : null}
      </div>
    </div>
  );
}

export default function Evidence({ subjects, canWrite, forcedReview, businessContext, onChanged }) {
  const s = useStyles();
  const withItems = SUBJECTS.filter((x) => (subjects?.[x.key] || []).length);
  const [type, setType] = useState((withItems[0] || SUBJECTS[0]).key);
  const [showReviewed, setShowReviewed] = useState(false);
  // Long cases render the first few open items; the rest on request.
  const [showAll, setShowAll] = useState(false);
  const items = subjects?.[type] || [];
  const open = items.filter(isOpenItem);
  const reviewed = items.filter((i) => !isOpenItem(i));
  const FIRST = 5;
  const shownOpen = showAll ? open : open.slice(0, FIRST);
  const moreN = open.length - shownOpen.length;

  return (
    <Section title="Evidence">
      {withItems.length > 1 ? (
        <div className={s.switch}>
          {withItems.map((x) => (
            <button
              key={x.key}
              type="button"
              className={`${s.switchOpt} ${type === x.key ? s.switchOn : ""}`}
              onClick={() => {
                setType(x.key);
                setShowReviewed(false);
                setShowAll(false);
              }}
            >
              {x.label} {(subjects[x.key] || []).length}
            </button>
          ))}
        </div>
      ) : null}
      {!items.length ? (
        <div className={s.empty}>Nothing flagged.</div>
      ) : (
        <>
          {open.length || showReviewed ? (
          <div className={s.list}>
            {shownOpen.map((item) => (
              <Item
                key={item._id}
                item={item}
                canWrite={canWrite}
                canDelete={type === "whatsapp_template"}
                forced={forcedReview || []}
                businessContext={businessContext}
                onChanged={onChanged}
              />
            ))}
            {moreN > 0 ? (
              <Box className={s.toggle} style={{ marginBottom: 14 }}>
                <TextAction inline tone="primary" onClick={() => setShowAll(true)}>
                  Show {moreN} more {moreN === 1 ? subjectOf(type).single : subjectOf(type).plural}
                </TextAction>
              </Box>
            ) : null}
            {showReviewed
              ? reviewed.map((item) => <Item key={item._id} item={item} canWrite={canWrite} forced={forcedReview || []} onChanged={onChanged} />)
              : null}
          </div>
          ) : null}
          {!open.length && !showReviewed ? <div className={s.empty} style={{ marginTop: 12 }}>Nothing open; every item here has been reviewed.</div> : null}
          {reviewed.length ? (
            <Box className={s.toggle}>
              <TextAction inline tone="primary" onClick={() => setShowReviewed((v) => !v)}>
                {showReviewed ? "Hide reviewed" : `Reviewed (${reviewed.length})`}
              </TextAction>
            </Box>
          ) : null}
        </>
      )}
    </Section>
  );
}
